import React from "react";
import { styled, Box } from "@mui/material";
import {
  formatMoney,
  hexadecimal,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import BoltIcon from "@mui/icons-material/Bolt";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";

const SvgContainer = styled("svg")(({ theme }) => ({
  width: 48,
  height: 48,
  fill: theme.palette.primary.main,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    width: 40,
    height: 40,
  },
}));

export const HighlightIcon = () => {
  return (
    <SvgContainer xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 9.978">
      <path d="m9.808 4.725-1.435-.41a3.922 3.922 0 0 1-2.71-2.711L5.253.169a.274.274 0 0 0-.506 0l-.41 1.435a3.922 3.922 0 0 1-2.712 2.711l-1.435.41a.263.263 0 0 0 0 .506l1.435.41a3.922 3.922 0 0 1 2.711 2.711l.41 1.435a.263.263 0 0 0 .506 0l.41-1.435a3.922 3.922 0 0 1 2.711-2.711l1.435-.41a.263.263 0 0 0 0-.506z" />
    </SvgContainer>
  );
};

const Container = styled(Box)(({ theme }) => ({
  borderRadius: 8,
  flex: 1,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  boxShadow: `0 2px 8px ${hexadecimal(theme.palette.primary.main)(10)}`,

  "&.reward": {
    padding: 24,
    gap: 16,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 8,
      padding: 18,
    },
  },
  "&.benefit": {
    padding: 24,
    gap: 16,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 10.5,
      padding: 18,
    },
  },

  ".rewards_header": {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.primary.main,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },

  ".benefits_header": {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.primary.main,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
  ".rewards": {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 4,
    },
  },
  ".benefits": {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  ".benefit-row": {
    display: "flex",
    alignItems: "center",
    gap: 8,

    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.primary.main,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
  },
}));

export const RewardExpandContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  width: "100%",
  gap: 16,
  marginTop: 31,
  [theme.breakpoints.down("md")]: {
    marginTop: 20,
    gap: 8,
    gridTemplateColumns: "1fr",
  },
}));

export const AccountConnectRewards = ({
  purchaseAmount,
  connectAmount,
  membershipAmount,
  retailerId,
  ...props
}: {
  purchaseAmount?: number;
  connectAmount?: number;
  membershipAmount?: number;
  retailerId?: number;
} & React.ComponentProps<typeof Container>) => {
  if (!retailerId) {
    return null;
  }

  if (!purchaseAmount && !connectAmount && !membershipAmount) {
    return null;
  }

  const totalAmount =
    (purchaseAmount ?? 0) + (connectAmount ?? 0) + (membershipAmount ?? 0);

  if (totalAmount === 0) {
    return null;
  }

  return (
    <Container className="reward" {...props}>
      <div className="rewards_header">
        Earn {formatMoney(totalAmount)} in rewards when you connect
      </div>
      <div className="rewards">
        {purchaseAmount ? (
          <BenefitRow
            text={
              <>
                <strong>{formatMoney(purchaseAmount)}</strong> in purchase
                rewards
              </>
            }
          />
        ) : (
          <BenefitRow text={<>Get cash back on purchases</>} />
        )}
        {connectAmount ? (
          <BenefitRow
            text={
              <div>
                <strong>{formatMoney(connectAmount)}</strong> in connect rewards
              </div>
            }
          />
        ) : (
          <BenefitRow text={<>Earn rewards to connect accounts</>} />
        )}
        {membershipAmount ? (
          <BenefitRow
            text={
              <>
                <strong>{formatMoney(membershipAmount)}</strong> in membership
                rewards
              </>
            }
          />
        ) : (
          <BenefitRow text={<>Access member-only offers</>} />
        )}
      </div>
    </Container>
  );
};

const BenefitRow = ({ text }: { text: string | React.ReactNode }) => {
  return (
    <div className="benefit-row">
      <GradeOutlinedIcon />
      {text}
    </div>
  );
};

const benefitList = [
  {
    icon: <BoltIcon />,
    title: "Connect in seconds",
    description:
      "Thousands of apps trust Brandclub to quickly connect to retailers",
  },
  {
    icon: <SecurityOutlinedIcon />,
    title: "Keep your data safe",
    description:
      "Brandclub uses best-in-class encryption to help protect your data",
  },
  {
    icon: <LocalShippingOutlinedIcon />,
    title: "Fast and Free Shipping",
    description: "Enjoy quick and free shipping on eligible orders",
  },
  {
    icon: <CurrencyExchangeOutlinedIcon />,
    title: "Easy returns and refund",
    description: "Hassle-free returns and quick refunds on eligible items",
  },
];

const BenefitCardContainer = styled(Container)(({ theme }) => ({
  background: "#fff",
  border: `1px solid ${hexadecimal(theme.palette.primary.main)(10)}`,
  boxShadow: `0 2px 12px ${hexadecimal(theme.palette.primary.main)(8)}`,
  padding: 0,

  maxWidth: 480,
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  overflow: "hidden",
  color: theme.palette.primary.main,
  [theme.breakpoints.down("xs")]: {
    maxHeight: 300,
  },
}));

const BenefitsContent = styled("div")(({ theme }) => ({
  overflowY: "auto",
  flex: 1,
  paddingBottom: 8,
  paddingTop: 8,
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "13px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gridTemplateColumns: "1fr",
    gap: "10px",
  },
  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: hexadecimal(theme.palette.primary.main)(20),
    borderRadius: 2,
  },
}));

const BenefitCardRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 16,

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: 10,
  },
}));

const BenefitCardText = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  color: theme.palette.primary.main,
}));

const BenefitCardTitle = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  color: theme.palette.primary.main,
  marginBottom: 2,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 14,
  },
}));

const BenefitCardDescription = styled("div")(({ theme }) => ({
  fontWeight: 400,
  fontSize: 15,
  color: theme.palette.text.secondary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 13,
  },
}));

export const RetailerConnectBenefits = ({
  rewardAmount,
}: {
  rewardAmount?: number;
}) => {
  return (
    <BenefitCardContainer className="benefit">
      <BenefitsContent>
        {benefitList.map((benefit) => (
          <BenefitCardRow key={benefit.title}>
            {benefit.icon}
            <BenefitCardText>
              <BenefitCardTitle>{benefit.title}</BenefitCardTitle>
              <BenefitCardDescription>
                {benefit.description}
              </BenefitCardDescription>
            </BenefitCardText>
          </BenefitCardRow>
        ))}
        {rewardAmount && rewardAmount > 0 ? (
          <BenefitCardRow>
            <GradeOutlinedIcon />
            <BenefitCardText>
              <BenefitCardTitle>
                Earn a bonus reward of {formatMoney(rewardAmount)} today!
              </BenefitCardTitle>
              <BenefitCardDescription>
                Get extra rewards for connecting your account now
              </BenefitCardDescription>
            </BenefitCardText>
          </BenefitCardRow>
        ) : null}
      </BenefitsContent>
    </BenefitCardContainer>
  );
};
